import { i18n } from '../i18n.js';
/**
 * UI Board Module
 * Handles game board rendering and interactions
 */

import { ActionIntent } from './action-intent.js';
import { CardDescEngraving } from './card-desc-engraving.js';
import { CardDrag } from './card-drag.js';
import { CardPlay } from './card-play.js';
import { Cards } from './cards.js';
import { Env } from './env.js';
import { GameEnd } from './game-end.js';
import { Indicators } from './indicators.js';
import { Mulligan } from './mulligan.js';
import { StoryFeed } from './story-feed.js';
import { ThinkingIndicator } from './thinking-indicator.js';
import { TurnTimer } from './turn-timer.js';
import { TouchPreview } from './touch-preview.js';
import { Tutorial } from './tutorial.js';
import { GameState } from '../game-state.js';
import { SocketHandler } from '../socket-handler.js';
let Board = {};

// Hover-engine branching (historic name: `_isSafari`). True means "use the
// transform: scale() fallback via --card-hover-transform" — originally
// because legacy Safari's CSS zoom didn't scale absolutely-positioned
// children. Env (env.js) now answers with a runtime capability probe, so
// modern WebKit graduates to the crisp zoom path; the UA fork survives only
// as a load-order-hiccup fallback.
const _isSafari = Env
    ? !Env.useZoomHover
    : (/Safari/i.test(navigator.userAgent) && !/Chrome/i.test(navigator.userAgent));

// --- The description keeps its resting lines while the card is enlarged ------
// Animating CSS `zoom` re-lays-out the `.card-desc` text every frame, and a
// line sitting on its wrap point tips a word onto another line at some zoom
// levels (Wicked Witch, 2026-07; Ember Artisan, 2026-09 at the owner's 1.25
// desktop scale in Firefox and Chrome). The resting lines are engraved when
// the grow starts FROM REST and stay engraved through the hold and the shrink;
// they are released only when the card is back at zoom 1. Releasing at full
// size — the July version — let the fully grown card re-wrap as it settled,
// which is the Ember Artisan report. The engraving itself (the same paragraph
// with its breaks forced, byte-identical text on release) is shared with every
// other enlargement: card-desc-engraving.js.
function engraveRestingDescription(cardElement, fromZoom) {
    // Only a card at rest shows its resting lines; one caught mid-shrink is
    // still engraved from when its grow began.
    if (!(Math.abs((Number(fromZoom) || 1) - 1) < 1e-3)) return;
    CardDescEngraving.engrave(cardElement);
}

function releaseRestingDescription(cardElement) {
    CardDescEngraving.release(cardElement);
}

/* The engraving an ENLARGED card hands to the same card rebuilt in its slot
   mid-hover (renderBoard carries the hover on). The successor is born at the
   carried scale and never rests, so the rule above never engraves it: laid out
   afresh at the enlarged size it would re-wrap. It adopts these resting lines
   and title instead, as a held hand card's replacement does (card-hover.js).
   Read from the engraving itself: the predecessor has already left the
   document, so nothing can be measured. Null when nothing is engraved. */
function engravingOf(cardElement) {
    const description = CardDescEngraving.isEngraved(cardElement);
    const title = CardDescEngraving.isTitleEngraved(cardElement);
    if (!description && !title) return null;
    return {
        layout: description ? CardDescEngraving.layoutOf(cardElement) : null,
        fontSize: description ? (cardElement.querySelector('.card-desc')?.style.fontSize || '') : '',
        title: title ? CardDescEngraving.titleStateOf(cardElement) : null,
    };
}

// --- Highlight utility ---
//
// Was `window.HighlightUtil`, guarded by `if (!window.HighlightUtil)` because
// under <script> tags two files could race to define it. It is a normal module
// export now: nothing races, and card-drag.js imports it by name.
const HighlightUtil = {
    addPotentialTargetHighlights: function (draggedId) {
        const selector = '#player-board .card, #opponent-board .card, #player-hand .card, #opponent-hand .card, #player-deck, #opponent-deck, .player-portrait';
        document.querySelectorAll(selector).forEach(el => {
            // Skip element being dragged
            if (draggedId && el.dataset && (el.dataset.instanceId === draggedId || el.dataset.cardId === draggedId)) return;
            el.classList.add('potential-target');
        });
    },
    clearPotentialTargetHighlights: function () {
        document.querySelectorAll('.potential-target').forEach(el => el.classList.remove('potential-target'));
    }
};


// Create UI namespace if it doesn't exist

// Board module
Board = (function () {
    const cardPresentationSettlements = new WeakMap();

    function uiText(key, fallback, replacements = {}) {
        const translated = i18n?.t?.(key, replacements);
        /* The FALLBACK is interpolated too. It is returned verbatim whenever
           i18n has not resolved — the normal case on a cold load — and the
           ui.json wording these fallbacks mirror carries `{{name}}`
           placeholders, so a user could be shown a literal `{{count}}`. Both
           brace styles are handled because the copy uses both. */
        const source = translated && translated !== key ? translated : fallback;
        return Object.entries(replacements || {}).reduce(
            (text, [name, value]) => String(text)
                .split(`{{${name}}}`).join(String(value))
                .split(`{${name}}`).join(String(value)),
            String(source == null ? key : source),
        );
    }

    function findPresentedBoardCard(instanceId) {
        const id = String(instanceId || '');
        return [...document.querySelectorAll(
            '#player-board .card[data-instance-id], #opponent-board .card[data-instance-id]'
        )].find((card) => card.dataset.instanceId === id) || null;
    }

    function waitForBoardMutation() {
        if (typeof MutationObserver !== 'function') return null;
        const roots = [
            document.getElementById('player-board'),
            document.getElementById('opponent-board'),
        ].filter(Boolean);
        if (!roots.length) return null;
        let observer;
        const promise = new Promise((resolve) => {
            observer = new MutationObserver(resolve);
            roots.forEach((root) => observer.observe(root, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['class', 'data-card-art-presentation'],
            }));
        });
        return {
            promise,
            cancel: () => observer?.disconnect(),
        };
    }

    async function followCardPresentation(instanceId) {
        while (true) {
            const cardElement = findPresentedBoardCard(instanceId);
            if (!cardElement) return null;
            if (!cardElement.classList.contains('card-summon-art-pending')) {
                return cardElement;
            }

            const settlement = cardPresentationSettlements.get(cardElement);
            const mutation = waitForBoardMutation();
            if (settlement && mutation) {
                await Promise.race([
                    Promise.resolve(settlement).catch(() => false),
                    mutation.promise,
                ]);
                mutation.cancel();
            } else if (settlement) {
                await Promise.resolve(settlement).catch(() => false);
            } else if (mutation) {
                await mutation.promise;
                mutation.cancel();
            } else {
                return cardElement;
            }
        }
    }

    function waitForCardPresentation(instanceId) {
        const cardElement = findPresentedBoardCard(instanceId);
        if (!cardElement || !cardElement.classList.contains('card-summon-art-pending')) {
            return cardElement;
        }
        return followCardPresentation(instanceId);
    }

    function pointerFromEvent(event) {
        if (event && typeof event.clientX === 'number' && typeof event.clientY === 'number') {
            return {
                x: Math.round(event.clientX),
                y: Math.round(event.clientY)
            };
        }
        return null;
    }

    function hoverBlockReason() {
        if (document.body.classList.contains('card-dragging')) return 'card-dragging';
        if (document.body.classList.contains('drop-settling')) return 'drop-settling';
        return null;
    }

    /**
     * Render the complete game state
     * @param {Object} data - Game state data from server
     */
    function renderGameState(data) {
        if (window.CLIENT_DEBUG_MODE) console.log('Full game state data:', data);

        // Check if data has the right structure
        if (!data || !data.gameState) {
            console.error('Invalid game state data structure', data);
            return;
        }

        // Check for game over conditions (health at 0)
        checkGameOver(data.gameState);

        // Update turn information
        const turnNumber = document.getElementById('turn-number');
        const activePlayer = document.getElementById('active-player');
        const endTurnBtn = document.getElementById('end-turn-btn');

        /* A REPEATED STATE IS RECONCILED, NEVER REWRITTEN (round 15, 28
           September 2026). The server publishes the first turn twice (the
           keep's broadcast, then the turn-clock sweep's first sight of the
           match 15-960 ms later), and every broadcast re-rendered this line:
           the same text replaced its own text nodes, End Turn was disabled
           again, and `classList.add/remove` rewrote the page root's class
           even when nothing changed, which wakes audio-settings.js's body
           observer into a layout solve. Only a change is written now. */
        const setText = (node, text) => {
            if (node.textContent !== text) node.textContent = text;
        };
        const setDisabled = (button, disabled) => {
            if (button.disabled !== disabled) button.disabled = disabled;
        };

        // Update turn display
        setText(turnNumber, uiText(
            'manga.nav.turnLabel',
            `Turn ${data.gameState.currentTurn}`,
            { n: data.gameState.currentTurn }
        ));

        // Handle mulligan phase display (`toggle` with a force writes only a change).
        const isMulliganPhase = data.gameState.state === 'mulligan';
        document.body.classList.toggle('mulligan-phase', isMulliganPhase);
        if (isMulliganPhase) {
            // Show contextual message: if mulligan overlay is closed, player has submitted
            const mulliganOverlayActive = Mulligan?.isActive?.();
            if (mulliganOverlayActive) {
                // Key-aware, same reason as the `t` helper further down.
                const mulliganTitle = i18n?.t?.('mulligan.title');
                setText(activePlayer, mulliganTitle && mulliganTitle !== 'mulligan.title'
                    ? mulliganTitle
                    : 'Mulligan Phase');
            } else {
                setText(activePlayer, uiText('game.waitingForOpponent', 'Waiting for opponent…'));
            }
            setDisabled(endTurnBtn, true);
        } else {
            // Disable end turn button if it's not the player's turn OR if an action is in progress
            const actionInProgress = data.gameState.actionInProgress || false;
            setDisabled(endTurnBtn, !data.gameState.isYourTurn || actionInProgress);

            // Read the bare `GameState` global (a top-level const, NOT a window
            // property) to match every other access in this file.
            const silent = !!(typeof GameState !== 'undefined'
                && GameState.get && GameState.get().actionIndicatorSilent);
            setText(activePlayer, activePlayerStatusText(data.gameState, silent));
        }

        // Handle story history
        handleStoryHistory(data);

        // Store important game state with safety checks for reconnection scenarios
        const gameStateUpdate = {
            state: data.gameState.state,
            winner: data.gameState.winner || null,
            endReason: data.gameState.endReason || null,
            currentTurn: data.gameState.currentTurn,
            isYourTurn: data.gameState.isYourTurn,
            battleLocation: data.gameState.battleLocation,
            summonedThisTurn: data.gameState.summonedThisTurn || [],
            usedThisTurn: data.gameState.usedThisTurn || [],
            player: data.gameState.player,
            opponent: data.gameState.opponent,
            // Own pending action-intent request, published by the server so a
            // client that lost the dialog can rebuild it. Dropping it here
            // silently disables recovery, which is exactly the deadlock this
            // whole path exists to prevent.
            actionIntent: data.gameState.actionIntent || null
        };

        // The opponentId logic below might be redundant if opponent object is now fully passed,
        // but let's keep it for safety in case opponent object is sometimes partial.
        // A better approach might be to ensure data.gameState.opponent is always fully populated or null.
        if (data.gameState.opponent && data.gameState.opponent.id) {
            // gameStateUpdate.opponentId = data.gameState.opponent.id; // Already part of opponent object
        } else if (GameState.get().opponent && GameState.get().opponent.id) {
            // If the current update's opponent is missing/incomplete, but we had a full one before,
            // we might want to preserve parts of the old opponent state if not overwritten by a null opponent.
            // However, directly passing data.gameState.opponent should handle this.
            // If data.gameState.opponent is null/undefined, GameState.opponent will be set to that.
            if (window.CLIENT_DEBUG_MODE) console.log('Preserving existing opponent data if new data is incomplete - though direct assignment should handle this.');
        }

        if (window.CLIENT_DEBUG_MODE) console.log('[Board.renderGameState] Updating GameState with payload:', gameStateUpdate);
        GameState.update(gameStateUpdate);
        if (ActionIntent && typeof ActionIntent.syncTurn === 'function') {
            ActionIntent.syncTurn(gameStateUpdate.currentTurn);
        }
        // Every broadcast is a recovery point. If the server still holds a
        // pending intent for us and the dialog is gone — tab switch, reload,
        // phone killing the backgrounded tab, socket reconnect — rebuild it
        // here. Without this the player is left on a board that refuses every
        // action, because the server keeps `actionInProgress` set until the
        // request is answered.
        if (ActionIntent && typeof ActionIntent.restoreFromGameState === 'function') {
            try {
                ActionIntent.restoreFromGameState(gameStateUpdate);
            } catch (_) { /* recovery is best effort; never break the render */ }
        }

        // Display-only countdown against the server's authoritative deadline.
        TurnTimer.update(data.gameState);

        if (Cards && typeof Cards.syncStellarBoardSummons === 'function') {
            Cards.syncStellarBoardSummons(data.gameState);
        }

        // Setup health, mana and deck indicators first
        Indicators.setupIndicators(data);

        // Reflect whose mind is at work on the portrait thinking indicators
        if (ThinkingIndicator && typeof ThinkingIndicator.sync === 'function') {
            ThinkingIndicator.sync(data.gameState);
        }

        // Setup all drop targets
        setupDeckDropTargets();
        setupBoardDropTargets();
        Indicators.setupPortraitDropTargets();

        // A player summon moves a card from hand to board in one authoritative
        // snapshot. Preserve the exact settled hand paint while that source is
        // still connected, then build the board first so its very first visible
        // frame can carry those pixels. Gameplay state is never delayed; this
        // is a downstream presentation hand-off only.
        const handPresentationSources = new Map(
            [...document.querySelectorAll('#player-hand .card[data-instance-id]')]
                .map((card) => [card.dataset.instanceId, card]),
        );

        renderBoard(
            document.getElementById('player-board'),
            data.gameState.player.summonedCards || [],
            true,
            { presentationSources: handPresentationSources },
        );

        renderBoard(
            document.getElementById('opponent-board'),
            data.gameState.opponent.summonedCards || [],
            false,
        );

        // Render player hand - with safety check
        if (Cards && typeof Cards.renderPlayerHand === 'function') {
            Cards.renderPlayerHand(data.gameState.player.hand || []);
        } else {
            console.error('ERROR: Cards.renderPlayerHand is not available!');
            // Create a fallback method to at least show the cards
            const playerHand = document.getElementById('player-hand');
            if (playerHand) {
                playerHand.innerHTML = ''; // Clear existing content
                const cards = data.gameState.player.hand || [];
                cards.forEach(card => {
                    const cardElement = document.createElement('div');
                    cardElement.className = 'card card-front';
                    if (card.type === 'Spell') {
                        cardElement.classList.add('spell-card');
                    } else if (card.type === 'Character') {
                        cardElement.classList.add('character-card');
                    }
                    cardElement.textContent = card.name;
                    cardElement.dataset.cardId = card.instanceId;
                    playerHand.appendChild(cardElement);
                });
            }
        }

        if (Cards && typeof Cards.attachStagedSummonVideos === 'function') {
            Cards.attachStagedSummonVideos();
        }

        // Render opponent hand (face down, except storyteller-revealed cards)
        if (Cards && typeof Cards.renderOpponentHand === 'function') {
            Cards.renderOpponentHand(
                data.gameState.opponent.handSize || 0,
                data.gameState.opponent.revealedCards || []
            );
        } else {
            console.error('ERROR: Cards.renderOpponentHand is not available!');
            // Create a fallback method to at least show the cards
            const opponentHand = document.getElementById('opponent-hand');
            if (opponentHand) {
                opponentHand.innerHTML = ''; // Clear existing content
                const handSize = data.gameState.opponent.handSize || 0;
                for (let i = 0; i < handSize; i++) {
                    const cardElement = document.createElement('div');
                    cardElement.className = 'card card-back';
                    cardElement.style.margin = '0 2.5%'; // Use percentage-based margin
                    opponentHand.appendChild(cardElement);
                }
            }
        }

        // Apply tutorial restrictions if in tutorial mode
        if (Tutorial?.isActive?.()) {
            Tutorial.applyForcedMoveRestrictions();
        }
    }

    /**
     * Reconcile the story feed against the authoritative server history.
     * Live beats arrive over storyUpdate; this path covers reconnect, undo
     * (server-shorter rebuild), and tutorial-manga hydration. The old
     * currentStory re-append safety net is gone on purpose: storyHistory
     * already contains every settled story, and the feed's reconcile is
     * count-based, so a duplicate append would desynchronize it.
     * @param {Object} data - Game state data from server
     */
    function handleStoryHistory(data) {
        const feed = StoryFeed;
        if (!feed) return; // some harnesses load the board without the feed

        if (data && Array.isArray(data.storyHistory)) {
            feed.reconcile(data.storyHistory);
        }

        // Grace sweep: pending cast tiles that this authoritative state no
        // longer accounts for dissolve instead of spinning forever.
        feed.onGameStateUpdated(data?.gameState || {});

        if (Array.isArray(data?.tutorialMangaPanels)) {
            data.tutorialMangaPanels.forEach(panel => {
                if (panel?.status === 'complete' && panel.imageUrl) {
                    // Hydration insert (no pending slot) — renders silently.
                    feed.onStoryImageUpdate(panel);
                }
            });
        }
    }

    const BOARD_SLOT_COUNT = 5;
    const BOARD_RASTER_RESERVE_SCALE = 4.8;

    /*
     * A SUMMON'S ART IS PREPARED ON THE PRESS.
     *
     * A played Character used to land with its art well empty for 74-281 ms
     * (measured 25 September 2026, Chrome, 110 ms RTT, on main as well): the
     * board card may only show its sharp, board-sized raster
     * (docs/card-rendering-regression-contract.md), and that raster was only
     * started once the server's snapshot had built the card. A press on a hand
     * Character now starts it: a hidden probe at the board's own geometry
     * settles it during the round trip, and renderBoard takes the probe's
     * settled paint as the new card's source. Presentation only — the play is
     * still the server's to accept, and a probe nobody summons is removed.
     */
    const SUMMON_PREP_TTL_MS = 15000;
    const SUMMON_PREP_LIMIT = 2;
    const summonPreparations = new Map(); // instanceId -> { wrapper, probe, ready, timer }

    function releaseSummonPreparation(instanceId) {
        const entry = summonPreparations.get(instanceId);
        if (!entry) return;
        summonPreparations.delete(instanceId);
        clearTimeout(entry.timer);
        entry.wrapper.remove();
    }

    function prepareSummonPresentation(instanceId, handCard = null) {
        const id = String(instanceId || '');
        if (!id || summonPreparations.has(id)) return;
        const state = GameState?.get?.();
        if (!state?.isYourTurn) return;
        const card = (state.player?.hand || []).find((entry) => String(entry?.instanceId) === id);
        const host = document.getElementById('game-view');
        if (!card || card.type !== 'Character' || !host) return;
        if (typeof Cards?.createCardElement !== 'function' || typeof Cards?.settleCardRasterForPresentation !== 'function') return;
        while (summonPreparations.size >= SUMMON_PREP_LIMIT) {
            releaseSummonPreparation(summonPreparations.keys().next().value);
        }
        // A battle card is one size in every zone (--card-unit is set for the
        // whole game), so a probe anywhere in the game view lays out exactly
        // like a seated card. It is never painted, never reachable, last in
        // document order, and carries no instance id, so nothing that looks a
        // card up by id or by zone can mistake it for the real one.
        const wrapper = document.createElement('div');
        wrapper.className = 'card-summon-prep';
        wrapper.setAttribute('aria-hidden', 'true');
        wrapper.setAttribute('inert', '');
        const probe = Cards.createCardElement(card, `summon-prep-${id}`, {
            rasterReserveScale: BOARD_RASTER_RESERVE_SCALE,
        });
        probe.removeAttribute('data-instance-id');
        probe.dataset.summonPrepFor = id;
        wrapper.appendChild(probe);
        host.appendChild(wrapper);
        const entry = { wrapper, probe, ready: false, timer: null };
        entry.timer = setTimeout(() => releaseSummonPreparation(id), SUMMON_PREP_TTL_MS);
        summonPreparations.set(id, entry);
        let settling;
        try {
            settling = Cards.settleCardRasterForPresentation(probe, card, {
                oversample: BOARD_RASTER_RESERVE_SCALE,
                sourceCard: handCard || null,
            });
        } catch (_) {
            settling = false;
        }
        Promise.resolve(settling).then((settled) => {
            if (summonPreparations.get(id) === entry) entry.ready = settled === true && probe.isConnected;
        }, () => {});
    }

    /*
     * A TAP ONLY INSPECTS; ONLY A PLAY IS PREPARED (round 15, 28 September 2026).
     *
     * The probe costs 146-171 ms of main thread on a 4x-CPU phone, and it used
     * to start on EVERY press of a hand card: in the task just before a tap's
     * pointerup, so a tap that only enlarges the card waited for a summon
     * nobody asked for (Chrome phone hand-card-enlarge 573/714/532 ms against
     * the baseline's 336/651/364 ms). Dragging is the only way to play
     * (cards.js applyHandCardRenderState), so the summon is prepared once the
     * gesture can be a play: the drag lifts (CardDrag carries a hand card, or
     * the finger left the tap's slop), or the press outlives a tap. The round
     * trip it races still starts at the drop, so the art is ready as before.
     * The held summon itself (hang over the lit slot, then slam) is not
     * touched: this only decides when its art starts settling.
     * Contract: tests/unit/ui/boardSummonPrep.test.js.
     */
    const SUMMON_PREP_TAP_MS = 250;
    const SUMMON_PREP_FALLBACK_SLOP_PX = 12;
    let summonPress = null; // { pointerId, instanceId, handCard, x, y, timer }

    function summonPrepSlopPx() {
        try {
            const published = CardDrag?.touchSlopPx?.();
            return Number.isFinite(published) ? published : SUMMON_PREP_FALLBACK_SLOP_PX;
        } catch (_) {
            return SUMMON_PREP_FALLBACK_SLOP_PX;
        }
    }

    // After the gesture's frame has painted: the press's (or the lift's) own
    // look comes first; the probe's raster work would otherwise hold it back.
    function prepareSummonAfterPaint(instanceId, handCard) {
        const later = () => setTimeout(() => prepareSummonPresentation(instanceId, handCard), 0);
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(later);
        else later();
    }

    function endSummonPress() {
        if (summonPress?.timer) clearTimeout(summonPress.timer);
        summonPress = null;
    }

    function samePointer(event) {
        return summonPress && (event?.pointerId ?? null) === summonPress.pointerId;
    }

    // The gesture became a play: prepare what it carries (a drag begun on the
    // enlarged card carries the hand card the popup shows), else what it pressed.
    function prepareSummonForPlay() {
        const press = summonPress;
        endSummonPress();
        let carried = null;
        try { carried = CardDrag?.getDragData?.() || null; } catch (_) { carried = null; }
        if (carried && carried.sourceType && carried.sourceType !== 'hand') return;
        const instanceId = String(carried?.instanceId || press?.instanceId || '');
        if (!instanceId) return;
        const handCard = press?.instanceId === instanceId
            ? press.handCard
            : [...document.querySelectorAll('#player-hand .card[data-instance-id]')]
                .find((node) => node.getAttribute('data-instance-id') === instanceId) || null;
        prepareSummonAfterPaint(instanceId, handCard);
    }

    function onHandPointerDown(event) {
        // A second finger is a pinch, never a play (CardDrag ends the first).
        if (event?.isPrimary === false) {
            endSummonPress();
            return;
        }
        endSummonPress();
        const target = event?.target;
        const handCard = target && typeof target.closest === 'function'
            ? target.closest('#player-hand .card[data-instance-id]')
            : null;
        // Every primary press is watched while it lasts: one on the enlarged
        // card lands beneath the pointer-transparent popup, never on the hand.
        summonPress = {
            pointerId: event?.pointerId ?? null,
            instanceId: handCard?.dataset?.instanceId || null,
            handCard,
            x: Number(event?.clientX) || 0,
            y: Number(event?.clientY) || 0,
            timer: null,
        };
        if (handCard) summonPress.timer = setTimeout(prepareSummonForPlay, SUMMON_PREP_TAP_MS);
    }

    function onSummonPressMove(event) {
        if (!samePointer(event)) return;
        let lifted = false;
        try { lifted = Boolean(CardDrag?.getDragData?.()); } catch (_) { lifted = false; }
        if (!lifted && summonPress.handCard) {
            const dx = (Number(event?.clientX) || 0) - summonPress.x;
            const dy = (Number(event?.clientY) || 0) - summonPress.y;
            lifted = Math.hypot(dx, dy) >= summonPrepSlopPx();
        }
        if (lifted) prepareSummonForPlay();
    }

    function onSummonPressEnd(event) {
        if (samePointer(event)) endSummonPress();
    }

    if (typeof document !== 'undefined') {
        // One set of listeners per page, even when the module is evaluated again.
        const LISTENER_KEY = '__moonfallSummonPrepListener';
        const previous = typeof window !== 'undefined' ? window[LISTENER_KEY] : null;
        if (previous && typeof previous === 'object') {
            Object.entries(previous).forEach(([type, listener]) => {
                document.removeEventListener(type, listener, true);
            });
        } else if (previous) {
            document.removeEventListener('pointerdown', previous, true);
        }
        const listeners = {
            pointerdown: onHandPointerDown,
            pointermove: onSummonPressMove,
            pointerup: onSummonPressEnd,
            pointercancel: onSummonPressEnd,
        };
        Object.entries(listeners).forEach(([type, listener]) => {
            document.addEventListener(type, listener, { capture: true, passive: true });
        });
        if (typeof window !== 'undefined') window[LISTENER_KEY] = listeners;
    }

    /* renderBoard's OWN per-render affordance classes. A reused element starts
       the next render still wearing them, so they are cleared and re-derived
       every time — otherwise a Character that has already acted keeps last
       render's legal-play ring. Classes owned by other modules (`dragging`,
       `hover-active`, enemy-replay's arrival opacity) are deliberately NOT in
       this list: their owners end them, and a rebuild used to end them by
       accident, which is what made an in-flight drag or glide flicker. */
    const BOARD_CARD_RENDER_CLASSES = [
        'legal-play',
        'summoned-this-turn',
        'used-this-turn',
        'draggable',
        'potential-target',
    ];

    // slotElement -> { cardElement, teardown, hoverState, resume } (bindSlotHover)
    const slotHoverBindings = new WeakMap();

    /* cardCenterer -> the instanceId whose motion it currently carries. Board FX
       animates the MOVER, not the card (fx.js `cardMover`), and the mover used
       to be destroyed on every snapshot — which is how a flourish ended and how
       fx.js knew to release it (`if (!mover.isConnected)`). Movers are permanent
       now, so "whose motion is this" has to be remembered rather than inferred
       from the node still existing. */
    const slotOccupants = new WeakMap();

    /**
     * End everything the departing occupant left on its mover.
     *
     * A WAAPI transform animation OVERRIDES the element's inline transform for
     * its whole run, so clearing the style is not enough: the animations
     * themselves have to be cancelled. Without this a card that dies
     * mid-`lungeCard` (~700-900ms) or mid-`neighborPush` (440ms) hands its
     * motion to the survivor that shifts into the slot, which then visibly
     * performs a dead card's attack. `transform-origin` is fx.js's too
     * (`host.style.transformOrigin = origin`) and now outlives the motion that
     * set it, so it is reset here for the same reason. Cancelling rejects the
     * Animation's `finished` promise, which is fx.js's own release path.
     */
    function endMoverMotion(cardCenterer) {
        // `getAnimations` is absent on older engines; the reconcile must not
        // depend on it, and a client without it simply keeps today's behaviour.
        const animations = cardCenterer.getAnimations?.() || [];
        animations.forEach((animation) => {
            try { animation.cancel(); } catch (_) { /* presentation only */ }
        });
        cardCenterer.style.transform = '';
        cardCenterer.style.transformOrigin = '';
    }

    /**
     * The five slots and their centerers, built ONCE per board and then kept.
     *
     * They used to be rebuilt on every authoritative snapshot, which detached
     * and re-attached every established card. On a DPR-3 phone that forced a
     * full relayout, re-shape and re-raster of cards nothing had happened to —
     * a 140-320ms main-thread stall on every summon, seen by the player as the
     * card descriptions blanking and coming back. Cards that did not change are
     * now never touched, so their layers are never invalidated.
     */
    function ensureSlotContainer(boardElement) {
        const existing = boardElement.querySelector('.card-slot-container');
        if (
            existing
            && existing.parentElement === boardElement
            && existing.children.length === BOARD_SLOT_COUNT
        ) return existing;

        const slotContainer = document.createElement('div');
        slotContainer.className = 'card-slot-container';
        for (let i = 0; i < BOARD_SLOT_COUNT; i++) {
            const slotElement = document.createElement('div');
            slotElement.className = 'card-slot';
            slotElement.dataset.slotIndex = i;
            slotElement.appendChild(createCardCenterer());
            slotContainer.appendChild(slotElement);
        }
        boardElement.replaceChildren(slotContainer);
        return slotContainer;
    }

    /**
     * A centered wrapper for the card. FX moves THIS node rather than the card,
     * so a board flourish and the card's own transform stack coexist instead of
     * overwriting each other (fx.js cardMover).
     */
    function createCardCenterer() {
        const cardCenterer = document.createElement('div');
        // Found by what it is, never by where it sits in the slot.
        cardCenterer.dataset.slotCenterer = '';
        cardCenterer.style.position = 'absolute';
        cardCenterer.style.top = '0';
        cardCenterer.style.left = '0';
        cardCenterer.style.width = '100%';
        cardCenterer.style.height = '100%';
        cardCenterer.style.display = 'flex';
        cardCenterer.style.justifyContent = 'center';
        cardCenterer.style.alignItems = 'center';
        return cardCenterer;
    }

    /* The mover is long-lived now, so it is no longer guaranteed by having just
       been built. Restore it rather than throwing a whole board render away if
       anything ever empties a slot. */
    function slotCenterer(slotElement) {
        /* By its marker, not as the slot's first child: anything else resting in
           a slot (a held summon's table shadow during its fall) would otherwise
           be taken for the card's mover, and the card seated inside it. */
        const existing = [...slotElement.children]
            .find((child) => child.dataset && Object.prototype.hasOwnProperty.call(child.dataset, 'slotCenterer'));
        if (existing) return existing;
        const cardCenterer = createCardCenterer();
        slotElement.insertBefore(cardCenterer, slotElement.firstChild);
        return cardCenterer;
    }

    /**
     * The id a board card wears while it occupies `slotIndex`.
     *
     * Under the reconciler an element can OUTLIVE the slot it was built for, so
     * this is applied on every seat rather than only at construction: a retained
     * card that shifts lanes would otherwise keep the id of the slot it left,
     * and a newly built card taking that slot would duplicate it. The fit-memory
     * surface key strips the trailing index (`cards.js`), so re-seating a card
     * changes its id without invalidating anything it has already measured.
     */
    function boardCardElementId(isPlayerBoard, slotIndex) {
        return `${isPlayerBoard ? 'player' : 'opponent'}-board-card-${slotIndex}`;
    }

    function boardCardSignature(card) {
        const signature = Cards?.cardRenderSignature;
        if (typeof signature !== 'function') return null;
        return signature(card, { rasterReserveScale: BOARD_RASTER_RESERVE_SCALE }) || null;
    }

    /**
     * Is this exact element already an exact rendering of `card`?
     *
     * Signature equality proves every painted field still matches. `ready`
     * proves the art pipeline finished with this node, so nothing downstream is
     * still waiting to mutate it. Everything else — a new card, changed
     * text/status/art, an in-flight or failed settlement, a client that loaded
     * without the signature helper — falls through to the full rebuild below
     * and keeps its existing contract exactly.
     */
    function isSettledBoardCard(previousCard, signature) {
        return Boolean(
            signature
            && previousCard
            && previousCard.dataset.cardArtPresentation === 'ready'
            && previousCard.dataset.cardRenderSignature === signature,
        );
    }

    /**
     * Build a fresh board generation for one card and decide how it may become
     * visible. Unchanged from the pre-reuse renderBoard: this is the path a
     * genuinely new summon, a changed card, or an unsettled one still takes.
     */
    function buildBoardCard(card, elementId, previousCard, signature, presentationSources, pendingPresentations) {
        /* A REAL id, not `undefined`.
           Passing undefined set `cardElement.id = undefined`, which the DOM
           stringifies — so all six board cards rendered with the literal
           id="undefined", sharing one invalid id, and getElementById returned
           whichever came first. It also degraded the description fit-memory:
           its key is `${surface}|${instance}` where surface is derived from the
           element id, so every board card claimed the same surface and the two
           boards could not be told apart. */
        const cardElement = Cards.createCardElement(card, elementId, {
            rasterReserveScale: BOARD_RASTER_RESERVE_SCALE,
        });
        if (signature) cardElement.dataset.cardRenderSignature = signature;
        const instanceId = String(card.instanceId);
        // Still waiting unseen for its landing (fx.js playEntrance): so is its copy.
        if (previousCard?.dataset.fxArrivalWait) cardElement.dataset.fxArrivalWait = previousCard.dataset.fxArrivalWait;
        const previousWasPending = previousCard?.classList.contains('card-summon-art-pending');
        const previousWasError = previousCard?.classList.contains('card-summon-art-error');
        const transfer = Cards.transferSettledRasterPaint;
        let hasSettledPresentation = false;
        let hasTransferredZonePaint = false;
        if (previousCard && typeof transfer === 'function') {
            const transferred = transfer(previousCard, cardElement);
            Cards.transferCardPresentation?.(previousCard, cardElement);
            // A pending source may already own a provisional canvas while
            // its awaited connected-geometry check is still outstanding.
            // Copying those pixels preserves continuity, but cannot promote
            // the replacement past the same presentation gate.
            hasSettledPresentation = transferred
                && !previousWasPending
                && !previousWasError;
        } else if (typeof transfer === 'function') {
            const presentationSource = presentationSources.get(instanceId);
            if (presentationSource) {
                // Hand pixels seed the detached board generation so no
                // empty IMG ever paints. The target is still a new board
                // node, however, and must revalidate its connected 4.8x
                // board geometry before becoming visible.
                hasTransferredZonePaint = transfer(presentationSource, cardElement) === true;
            }
        }
        if (hasSettledPresentation) {
            cardElement.dataset.cardArtPresentation = 'ready';
        } else {
            // A new card, a still-pending generation, or an established
            // card whose settled/error paint could not be transferred is
            // revalidated. A previous terminal error is already truthful,
            // visible presentation: preserve that well while retrying so
            // permanent failures do not withdraw the card on every board
            // rebuild. A recovered/replaced source can still promote this
            // generation to ready when fresh settlement succeeds.
            if (previousWasError) {
                cardElement.classList.add('card-summon-art-error');
                cardElement.dataset.cardArtPresentation = 'error';
            } else if (previousCard && !previousWasPending) {
                /* ESTABLISHED card whose settled pixels could not transfer
                   into the rebuilt DOM. It has stood visible on this board
                   for whole turns; pushing it through the invisible pending
                   gate hid the ENTIRE card — text included — for as long as
                   the 4.8x raster took to revalidate ("card descriptions
                   disappearing for a second on mobile when a card is
                   summoned"). Text-first (docs/asset-loading.md law 2): it
                   stays fully visible with its art well's own loading
                   treatment while settlement below refreshes the raster. */
                cardElement.dataset.cardArtPresentation = 'refreshing';
            } else {
                // Exposing a replacement's fresh loading IMG is not
                // continuity—the old pixels were discarded with the prior
                // DOM. Only cards without a truthful terminal presentation
                // use the invisible pending gate. Siblings remain visible.
                cardElement.classList.add('card-summon-art-pending');
                cardElement.dataset.cardArtPresentation = 'pending';
            }
            pendingPresentations.push({
                cardElement,
                card,
                // A successful hand transfer already supplied synchronous
                // pixels. Once inserted, layout reads are authoritative in
                // this same task, so it can revalidate before first paint
                // without waiting a slow engine's next animation frame.
                waitForLayout: !hasTransferredZonePaint,
            });
        }
        return cardElement;
    }

    /**
     * Everything renderBoard owns on a seated card, applied identically to a
     * freshly built element and to a retained one. Keeping the two paths on ONE
     * function is what guarantees a reused card cannot drift from a rebuilt one.
     */
    function applyBoardCardRenderState(cardElement, card, isPlayerBoard, slotIndex) {
        // Tag zone/owner for hint logic
        cardElement.dataset.zone = isPlayerBoard ? 'player-board' : 'opponent-board';
        cardElement.dataset.owner = isPlayerBoard ? 'player' : 'opponent';
        // Re-stamped per seat: see boardCardElementId. A retained card that
        // changed lanes must not carry its old slot's id into this render.
        cardElement.id = boardCardElementId(isPlayerBoard, slotIndex);

        // Ensure the card maintains its original dimensions and aspect ratio
        // Don't set explicit width/height - let the CSS variables handle it
        // This ensures cards on board match cards in hand exactly
        cardElement.style.flexShrink = '0'; // Prevent shrinking
        cardElement.style.flexGrow = '0'; // Prevent growing

        /* ONE way to enlarge, everywhere in battle: tap opens the same
           sharp popup the hand uses. The bare attach's synthetic-hover
           fallback drove the in-place transform zoom instead, which WebKit
           composites by STRETCHING a rasterised layer — "cards in game,
           like summoned, are blurry when hovered" on the phone. Board
           Characters act by dragging, so no tap action is declared.
           Re-attached every render so a RETAINED element's callbacks close
           over this snapshot's card object; TouchPreview registers its
           listener once and refreshes the options. */
        TouchPreview?.attach?.(cardElement, {
            onPreview: () => Cards?.showCardPreviewFor?.(cardElement, card),
            onDismiss: () => Cards?.clearCardPreview?.(),
        });

        // A retained element arrives wearing the PREVIOUS render's affordances.
        // Clear them, and the gesture that goes with them, before re-deriving.
        BOARD_CARD_RENDER_CLASSES.forEach((name) => cardElement.classList.remove(name));
        CardDrag.cleanupDraggable(cardElement);

        /* EVERY Character on the player's board can be picked up (owner,
           "Moonfall Drag Play Brief", 2026-09-24). One that cannot act — not
           your turn, summoning sickness, already acted — still lifts, says
           why while it is held, and comes home (CardPlay.boardRefusal). */
        if (isPlayerBoard) {
            setupBoardCardDragging(cardElement, card, slotIndex);
            if (GameState.get().isYourTurn) {
                if (GameState.canUseCard(card.instanceId)) {
                    cardElement.classList.add('legal-play');
                } else if (GameState.get().summonedThisTurn.includes(card.instanceId)) {
                    cardElement.classList.add('summoned-this-turn');
                } else if (GameState.get().usedThisTurn.includes(card.instanceId)) {
                    cardElement.classList.add('used-this-turn');
                }
            }
            /* A board card lifted by a drag in flight when this render rebuilt
               it stays hidden in its slot; CardDrag reveals it on landing. */
            if (CardDrag?.isCarrying?.(card.instanceId, 'board')) {
                cardElement.classList.add('dragging');
                cardElement.style.visibility = 'hidden';
            }
            /* THE SUMMON THE PLAYER IS STILL HOLDING. The server seated the card
               while its drag clone hangs over this slot; the landing takes the
               clone's place in one motion (fx.js claims it). Until then the
               seated copy stays unpainted so the card is never shown twice. */
            if (CardDrag?.isHeldSummon?.(card.instanceId)) {
                cardElement.dataset.dragHeldArrival = '1';
            } else {
                delete cardElement.dataset.dragHeldArrival;
            }
        }

        // Register this card as a drop target for CardDrag
        CardDrag.registerTarget(cardElement, {
            zone: isPlayerBoard ? 'player-board' : 'opponent-board',
            validate: (d) => {
                if (!d) return false;
                if (!GameState.get().isYourTurn) return false;
                if (d.instanceId === card.instanceId) return false;
                return true;
            },
            // No drop-time cue on the target. The strike is drawn when the
            // server accepts the action (characterActionStarted →
            // UI.FX.lungeAttack), and a target that rings at pointer
            // release has reacted ~370ms before anything hit it.
            onDrop: (d) => routeActionDrop(d, 'card', card.instanceId)
        });
    }

    /**
     * Render the board with cards
     * @param {HTMLElement} boardElement - The board container element
     * @param {Array} cards - Array of cards on the board
     * @param {boolean} isPlayerBoard - Whether this is the player's board
     * @param {Object} options - Presentation sources retained from another zone
     */
    function renderBoard(boardElement, cards, isPlayerBoard, options = {}) {
        const previousCards = new Map(
            [...boardElement.querySelectorAll('.card[data-instance-id]')]
                .map((card) => [card.dataset.instanceId, card]),
        );
        const presentationSources = new Map(options.presentationSources instanceof Map
            ? options.presentationSources
            : []);
        // A summon prepared on the press brings its settled board-sized paint.
        if (isPlayerBoard) {
            summonPreparations.forEach((entry, instanceId) => {
                if (entry.ready && entry.probe.isConnected) presentationSources.set(instanceId, entry.probe);
            });
        }
        const pendingPresentations = [];

        const slotContainer = ensureSlotContainer(boardElement);
        const slots = [...slotContainer.children];
        const isYourTurn = GameState.get().isYourTurn;
        slots.forEach((slotElement) => {
            /* Make the slot highlighted if it's the player's board and their
               turn. The playable state warms toward the board's OWN gold — the
               same family as the beaded lane divider and the summoner nameplate
               rims — rather than the cold lilac it used. An active drop target
               should look lit from the table's light, not tinted by a second
               accent colour the scene does not otherwise contain. */
            slotElement.style.background = isPlayerBoard && isYourTurn
                ? 'rgba(234, 199, 97, 0.10)'
                : '';
        });

        // Phase 1 — resolve the element that belongs in each slot.
        const seating = new Array(BOARD_SLOT_COUNT).fill(null);
        // One element can only sit in one slot. A duplicated instance id is a
        // server invariant violation, not a licence to seat a node twice.
        const claimed = new Set();
        cards.slice(0, BOARD_SLOT_COUNT).forEach((card, index) => {
            const previousCard = previousCards.get(String(card.instanceId));
            const signature = boardCardSignature(card);
            if (!claimed.has(previousCard) && isSettledBoardCard(previousCard, signature)) {
                claimed.add(previousCard);
                // Nothing this card paints has changed and its art is already
                // settled. Keep the exact node — its decoded paint, its stellar
                // video, its composited layer, its live animations. This is the
                // whole optimisation, and the only way to spend no raster.
                seating[index] = previousCard;
                return;
            }
            /* A replaced generation leaves the document BEFORE its successor is
               measured. transferSettledRasterPaint reads both nodes' geometry,
               and the old full wipe always handed it a detached predecessor; a
               still-connected one would feed it a rect it has never seen. */
            if (previousCard) previousCard.remove();
            seating[index] = buildBoardCard(
                card,
                boardCardElementId(isPlayerBoard, index),
                previousCard,
                signature,
                presentationSources,
                pendingPresentations,
            );
        });

        // Phase 2 — evict. Cards that left the board, and survivors that shifted
        // slot, vacate before anything is seated, so no slot is ever asked to
        // hold two cards at once.
        const retained = new Set(seating.filter(Boolean));
        previousCards.forEach((element) => {
            if (retained.has(element)) return;
            CardDrag.unregisterTarget(element);
            element.remove();
        });
        slots.forEach((slotElement, index) => {
            const occupant = slotCenterer(slotElement).firstElementChild;
            if (occupant && occupant !== seating[index]) occupant.remove();
        });

        // Phase 3 — seat, bind, and apply this render's state.
        slots.forEach((slotElement, index) => {
            const cardCenterer = slotCenterer(slotElement);
            const cardElement = seating[index];
            const occupantId = cardElement ? String(cards[index].instanceId) : null;
            const binding = slotHoverBindings.get(slotElement);
            let carriedHover = null;
            if (binding && binding.cardElement !== cardElement) {
                /* The SAME card rebuilt in this slot (a hex, a patched
                   description) takes over whatever its predecessor's hover was
                   doing: enlarged, still growing, shrinking away, or waiting
                   for its raster. Nothing else would restart it: the pointer
                   never leaves the slot, so no mouseenter comes, and the card
                   the player was reading collapsed to rest until they left and
                   came back. A different card taking the slot is handed
                   nothing (the departing one's unwind below still runs). */
                if (occupantId !== null && slotOccupants.get(cardCenterer) === occupantId) {
                    carriedHover = binding.hoverState();
                }
                binding.teardown();
                slotHoverBindings.delete(slotElement);
            }
            if (!cardElement) {
                /* The slot emptied. Whatever flourish its occupant left is now
                   running on a mover with nothing in it — and would be inherited
                   by the next card seated here. */
                if (slotOccupants.has(cardCenterer)) {
                    slotOccupants.delete(cardCenterer);
                    endMoverMotion(cardCenterer);
                }
                return;
            }
            /* Motion belongs to the OCCUPANT, not to the node. A different card
               taking this slot ends the previous one's motion; the SAME card
               merely rebuilt in place keeps it, which is exactly the resume
               fx.js re-binds for (a lane replaced ~100ms into an enemy attack
               must not freeze the strike in its wind-up). */
            if (slotOccupants.get(cardCenterer) !== occupantId) {
                endMoverMotion(cardCenterer);
                slotOccupants.set(cardCenterer, occupantId);
            }
            /* The carried enlargement goes on BEFORE insertion, so the card's
               first style is already the enlarged one: `.card` transitions
               `transform`, and on the transform engine a scale written after
               a style flush would replay the whole grow. Its predecessor's
               resting lines and title go on first, so it is never laid out
               with text wrapped at the enlarged size (engravingOf).
               TEXT THAT ENGRAVING DOES NOT SPELL (a patched description, a
               renamed card: adopt refuses it) is engraved from the card's OWN
               resting lines instead (review of the merge, 28 September 2026:
               left natural, it re-wrapped on every frame of the rest of its
               grow and of its shrink). It is laid out at rest in its slot, with
               its render state, where its first fits run and its lines are
               read, and the carried CSS zoom goes on after that in the same
               task: zoom is never transitioned, and nothing is painted in
               between. A transform scale leaves the layout at rest, so that
               engine still takes it before insertion.
               Contract: tests/unit/ui/boardHoverRebuildKeepsEngraving.test.js. */
            let engraveAtRest = false;
            if (carriedHover?.scale > 1) {
                if (carriedHover.engraving) CardDescEngraving.adopt(cardElement, carriedHover.engraving);
                engraveAtRest = !CardDescEngraving.isEngraved(cardElement)
                    || !CardDescEngraving.isTitleEngraved(cardElement);
                if (!engraveAtRest || _isSafari) writeHoverScale(cardElement, carriedHover.scale);
            }
            if (cardElement.parentElement !== cardCenterer) {
                cardCenterer.appendChild(cardElement);
            }
            if (!slotHoverBindings.has(slotElement)) {
                slotHoverBindings.set(slotElement, {
                    cardElement,
                    ...bindSlotHover(slotElement, cardElement),
                });
            }
            applyBoardCardRenderState(cardElement, cards[index], isPlayerBoard, index);
            if (engraveAtRest) {
                CardDescEngraving.engrave(cardElement);
                if (!_isSafari) writeHoverScale(cardElement, carriedHover.scale);
            }
            // A title still on a stand-in size is fitted before the next frame
            // paints it: a warm summon is seated and landed in this task.
            Cards.fitTitleBeforeFirstFrame?.(cardElement);
            // After the render state: the hint reads the zone it just stamped.
            if (carriedHover) slotHoverBindings.get(slotElement).resume(carriedHover);
        });

        pendingPresentations.forEach(({ cardElement, card, waitForLayout }) => {
            const finishPresentation = (settled) => {
                if (!cardElement.isConnected) return false;
                cardElement.classList.remove('card-summon-art-pending');
                if (settled) {
                    cardElement.classList.remove('card-summon-art-error');
                    cardElement.dataset.cardArtPresentation = 'ready';
                    return true;
                }
                cardElement.classList.add('card-summon-art-error');
                cardElement.dataset.cardArtPresentation = 'error';
                return false;
            };
            /* Pixels transferred from a source already settled at this very
               board geometry and tier (the summon prepared on the press) are
               revalidated now, in the task that inserted the card, so it is
               presented before its first paint. */
            if (!waitForLayout && Cards.isRasterPresentationCurrent?.(cardElement, { oversample: BOARD_RASTER_RESERVE_SCALE })) {
                finishPresentation(true);
                cardPresentationSettlements.set(cardElement, Promise.resolve(true));
                return;
            }
            let result = false;
            try {
                result = Cards.settleCardRasterForPresentation(
                    cardElement,
                    card,
                    { oversample: BOARD_RASTER_RESERVE_SCALE, stableGeometry: waitForLayout },
                );
            } catch (_) {
                result = false;
            }
            const settlement = Promise.resolve(result).then(
                finishPresentation,
                () => finishPresentation(false),
            );
            cardPresentationSettlements.set(cardElement, settlement);
        });
        // Never cohort-hold the board: one cold summon may not take established
        // Characters with it. A genuinely new card is held individually until
        // it can arrive with settled pixels (or a truthful terminal error well).
        // Contract: tests/unit/ui/boardSummonSnappiness.test.js.

        // A prepared summon is spent once its card is seated (its paint was
        // transferred above, or it was still settling and the ordinary path
        // took over) or once it has left the hand without being summoned.
        if (isPlayerBoard && summonPreparations.size) {
            const seated = new Set(cards.map((card) => String(card?.instanceId)));
            const inHand = new Set((GameState?.get?.()?.player?.hand || []).map((card) => String(card?.instanceId)));
            [...summonPreparations.keys()].forEach((instanceId) => {
                if (seated.has(instanceId) || !inHand.has(instanceId)) releaseSummonPreparation(instanceId);
            });
        }
    }

    /* The slot hover's enlargement as one number: CSS zoom on the zoom engine,
       `--card-hover-transform: scale()` on the transform engine (_isSafari). */
    function readHoverScale(cardElement) {
        if (_isSafari) {
            const raw = cardElement.style.getPropertyValue('--card-hover-transform');
            const m = raw && raw.match(/scale\(([\d.]+)\)/);
            return m ? parseFloat(m[1]) : 1;
        }
        return parseFloat(cardElement.style.zoom) || 1;
    }

    function writeHoverScale(cardElement, scale) {
        if (_isSafari) {
            cardElement.style.setProperty('--card-hover-transform', `scale(${scale})`);
        } else {
            cardElement.style.zoom = String(scale);
        }
    }

    function clearHoverScale(cardElement) {
        if (_isSafari) {
            cardElement.style.removeProperty('--card-hover-transform');
        } else {
            cardElement.style.zoom = '';
        }
    }

    /**
     * The hover engine for one board slot, bound to the (slot, card) PAIR.
     *
     * The listeners live on the SLOT — a fixed-size parent — instead of the
     * card: CSS zoom changes the card's visual bounds, which can push the
     * cursor outside it on corners and start a mouseleave → shrink →
     * mouseenter → zoom loop. The slot's bounds never change, so hover stays
     * stable. The returned teardown lets a slot hand its listeners to a
     * different occupant without leaking a live closure onto a card that has
     * left the board mid-hover.
     */
    function bindSlotHover(slotElement, cardElement) {
        // Attach dynamic hover scaling for this board card.
        // Chrome/Firefox: animate CSS zoom for crisp rasterization.
        // Safari: animate --card-hover-transform (zoom doesn't scale
        // absolutely-positioned children consistently in WebKit).
        let zoomAnim = null;
        let zoomTarget = 1;
        let deferredHoverCancel = null;
        let hoverIntentGeneration = 0;
        /* What this pair's hover is doing, so a rebuild of the same card can
           carry it on (hoverState / resume). `engaged`: an enlargement began
           and the pointer has not left. `pendingEntry`: an entry still waiting
           to begin one, on a raster preparation in flight or a replay queued
           behind a settling drop. An entry the engine REFUSED (a drag was in
           progress) is neither, so a rebuild never revives it. */
        let engaged = false;
        let pendingEntry = null;
        function animateZoom(from, to, duration, onDone) {
        if (zoomAnim) cancelAnimationFrame(zoomAnim);
        zoomTarget = to;
        // Engrave the resting lines as the grow leaves rest; they hold until
        // the card is back at zoom 1 (see engraveRestingDescription).
        if (to > from) engraveRestingDescription(cardElement, from);
        const start = performance.now();
        function tick(now) {
            // Card removed / re-rendered mid-grow (e.g. hexed): stop and
            // restore the natural text on the detached node.
            if (!cardElement.isConnected) {
                releaseRestingDescription(cardElement);
                zoomAnim = null;
                return;
            }
            // Abort animation while a card is being dragged or
            // a drop is still settling (board re-render fires
            // mouseenter on newly created slots under the cursor)
            const blockedBy = hoverBlockReason();
            if (blockedBy) {
                releaseRestingDescription(cardElement);
                clearHoverScale(cardElement);
                zoomAnim = null;
                engaged = false;
                return;
            }
            const elapsed = now - start;
            // Clamp t into [0, 1]. The first rAF timestamp can precede the
            // performance.now() captured at animate() start; a negative t
            // makes the ease overshoot below `from`, so the very first
            // frame renders zoom < 1 — the card visibly collapses/shrinks
            // for one frame before springing up (a "flicker while growing").
            // crisp-hover.js already guards this; board hover did not.
            const t = Math.max(0, Math.min(elapsed / duration, 1));
            // ease-out cubic
            const ease = 1 - Math.pow(1 - t, 3);
            const current = from + (to - from) * ease;
            writeHoverScale(cardElement, current);
            if (t < 1) {
                zoomAnim = requestAnimationFrame(tick);
            } else {
                zoomAnim = null;
                // Back at rest: the natural paragraph again (byte-identical).
                // A card that has only finished GROWING keeps its resting
                // lines — releasing them at full size re-wrapped it there.
                if (Math.abs(to - 1) < 1e-3) releaseRestingDescription(cardElement);
                if (onDone) onDone();
            }
        }
        zoomAnim = requestAnimationFrame(tick);
    }

    /* THE HINT STANDS WHERE THE CARD WILL STAY (flicker hunt, 25 September
       2026). cards.js places the side hint beside the card's CURRENT box, and
       the enlargement is this engine's own per-frame zoom, which it cannot see
       coming: placed in the task that started the grow, the pill appeared
       beside the card at rest and was then carried outward with it, frame by
       frame, for ~200 ms. It is placed in the frame the grow lands instead,
       with no wait beyond the zoom itself, and only for the hover that asked
       for it: a leave, a drag or a teardown cancels the grow, and its hint
       with it. Contract: tests/unit/ui/boardSideHintAfterZoom.test.js. */
    function hintPlacer(intentGeneration) {
        return () => {
            if (!engaged || intentGeneration !== hoverIntentGeneration || !cardElement.isConnected) return;
            if (Cards && typeof Cards.updateCardHint === 'function') {
                Cards.updateCardHint(cardElement);
            }
        };
    }

    function getPointer(event) {
        return pointerFromEvent(event) || (
            typeof window.lastMouseX === 'number' && typeof window.lastMouseY === 'number'
                ? { x: Math.round(window.lastMouseX), y: Math.round(window.lastMouseY) }
                : null
        );
    }

    /* ONE CSS PIXEL OF GRACE, FOR THE ROUNDING AT THE EDGE.
       The slot's rect is fractional (413.39px: every card size is vmin-derived)
       and a mouse event's clientX/Y is the integer the engine hands out. A
       pointer that crosses in at 413.5 hit-tests INSIDE — that is what fired
       the mouseenter — and reports 413, which the exact comparison read as
       outside; with the shared globals still one sample behind, both halves
       of the OR-guard failed and the hover silently never started. No second
       mouseenter comes while the pointer stays in, so the card sat at rest
       until the player left and came back. Reproduced on the opponent's
       freshly summoned card (PvP observer, real Chrome) with a stepped entry
       through the slot's left edge; fractional pointer positions are the
       everyday case under display scaling and browser zoom. A pointer a
       whole pixel or more outside is still outside — the deferred replay
       and the stale-global test below keep that rejection. */
    const SLOT_EDGE_TOLERANCE_PX = 1;

    function isPointerInsideSlot(pointer) {
        if (!pointer) return false;
        const rect = slotElement.getBoundingClientRect();
        return (
            pointer.x >= rect.left - SLOT_EDGE_TOLERANCE_PX &&
            pointer.x <= rect.right + SLOT_EDGE_TOLERANCE_PX &&
            pointer.y >= rect.top - SLOT_EDGE_TOLERANCE_PX &&
            pointer.y <= rect.bottom + SLOT_EDGE_TOLERANCE_PX
        );
    }

    function cancelDeferredHover() {
        if (!deferredHoverCancel) return;
        deferredHoverCancel();
        deferredHoverCancel = null;
        pendingEntry = null;
    }

    function startHover(event, intentGeneration = hoverIntentGeneration) {
        const pointer = getPointer(event);
        const beginEnlargement = () => {
            // Chromium dispatches mouseenter BEFORE the boundary-crossing
            // mousemove updates the shared pointer globals, so on a natural
            // entry getPointer() lags one sample outside the slot. Accept
            // the hover when EITHER the triggering event's coordinates or
            // the current global pointer sit inside; a genuine exit always
            // fires mouseleave, which bumps hoverIntentGeneration.
            const pointerNow = getPointer();
            const pointerLeft = pointer
                && !isPointerInsideSlot(pointer)
                && !(pointerNow && isPointerInsideSlot(pointerNow));
            if (
                intentGeneration !== hoverIntentGeneration
                || !slotElement.isConnected
                || !cardElement.isConnected
                || pointerLeft
            ) return;
            engaged = true;
            pendingEntry = null;
            const viewportHeight = window.innerHeight;
            const rect = slotElement.getBoundingClientRect();
            const spaceTop = rect.top;
            const spaceBottom = viewportHeight - rect.bottom;
            const availableSpace = Math.min(spaceTop, spaceBottom);
            let scale = 1 + (2 * availableSpace) / rect.height;
            const MAX_SCALE = 4.8;
            if (scale > MAX_SCALE) scale = MAX_SCALE;
            if (scale < 1) scale = 1;
            const from = readHoverScale(cardElement);
            const placeHint = hintPlacer(intentGeneration);
            // Already at its size (no room to grow, or back before the shrink
            // began): nothing will move, so the hint is placed at once.
            const grows = from !== scale;
            animateZoom(from, scale, 200, grows ? placeHint : undefined);
            if (Cards && typeof Cards.setStellarHover === 'function') {
                Cards.setStellarHover(cardElement, true);
            }
            if (!grows) placeHint();
        };

        const lock = Cards?.lockCardRaster;
        const ready = typeof lock !== 'function'
            || lock(cardElement, 'hover') !== false;
        if (ready) {
            beginEnlargement();
            return;
        }
        const prepare = Cards?.prepareCardRasterForInteraction;
        if (typeof prepare !== 'function') return;
        pendingEntry = { pointer, generation: intentGeneration };
        void Promise.resolve(prepare(cardElement, 'hover')).then((settled) => {
            if (pendingEntry?.generation === intentGeneration) pendingEntry = null;
            if (settled) beginEnlargement();
        });
    }

    function queueDeferredHover(event, blockedBy, intentGeneration) {
        if (blockedBy !== 'drop-settling') return;
        const pointer = getPointer(event);
        cancelDeferredHover();
        const replay = () => {
            cancelDeferredHover();
            const stillBlockedBy = hoverBlockReason();
            if (stillBlockedBy) {
                return;
            }
            if (!slotElement.isConnected || !cardElement.isConnected || !isPointerInsideSlot(pointer)) {
                return;
            }
            startHover({ clientX: pointer.x, clientY: pointer.y }, intentGeneration);
        };
        const timeoutId = setTimeout(replay, 650);
        const onSettlingEnded = () => replay();
        window.addEventListener('moonfall:drop-settling-ended', onSettlingEnded, { once: true });
        deferredHoverCancel = () => {
            clearTimeout(timeoutId);
            window.removeEventListener('moonfall:drop-settling-ended', onSettlingEnded);
        };
        pendingEntry = { pointer, generation: intentGeneration };
    }

        // Touch: tap enlarges (a board card acts by being dragged, never by a
        // second tap). The guard below already admits
        // the synthetic (untrusted) mouseenter this dispatches while rejecting
        // the compatibility one a real tap produces.
        TouchPreview?.attach?.(slotElement);

        const onSlotEnter = (event) => {
            const intentGeneration = ++hoverIntentGeneration;
            // TouchPreview owns tap inspection and its trailing
            // compatibility-mouse window.
            if (event?.isTrusted !== false && TouchPreview?.isSuppressingMouse?.()) return;
            // Don't start zoom animations during card drag or
            // while a drop is still settling
            const blockedBy = hoverBlockReason();
            if (blockedBy) {
                queueDeferredHover(event, blockedBy, intentGeneration);
                return;
            }
            cancelDeferredHover();
            startHover(event, intentGeneration);
        };

        const onSlotLeave = () => {
            hoverIntentGeneration += 1;
            engaged = false;
            pendingEntry = null;
            cancelDeferredHover();
            animateZoom(readHoverScale(cardElement), 1, 150, () => clearHoverScale(cardElement));
            if (Cards && typeof Cards.setStellarHover === 'function') {
                Cards.setStellarHover(cardElement, false);
            }
            const hint = cardElement.querySelector('.card-hint');
            if (hint) {
                hint.textContent = '';
                hint.classList.remove('visible-hint');
            }
            if (Cards?.hideSideHint) {
                Cards.hideSideHint(cardElement);
            }
        };

        slotElement.addEventListener('mouseenter', onSlotEnter);
        slotElement.addEventListener('mouseleave', onSlotLeave);

        /* Handing the slot to a different card: stop this pair's engine and
           return the departing card to rest. A rebuild used to achieve that by
           destroying the node; nothing else did, so the unwind has to be
           explicit now that the node survives. */
        const teardown = () => {
            slotElement.removeEventListener('mouseenter', onSlotEnter);
            slotElement.removeEventListener('mouseleave', onSlotLeave);
            hoverIntentGeneration += 1;
            engaged = false;
            cancelDeferredHover();
            pendingEntry = null;
            if (zoomAnim) cancelAnimationFrame(zoomAnim);
            zoomAnim = null;
            releaseRestingDescription(cardElement);
            cardElement.style.removeProperty('--card-hover-transform');
            cardElement.style.zoom = '';
        };

        /* What this pair's hover is doing right now, for the SAME card rebuilt
           into this slot to carry on (renderBoard reads it before teardown).
           Null when there is nothing to carry. */
        const hoverState = () => {
            const scale = readHoverScale(cardElement);
            // An enlarged card's resting lines go with it (engravingOf).
            const engraving = scale > 1 ? engravingOf(cardElement) : null;
            if (engaged) return { phase: 'engaged', scale, target: zoomAnim ? zoomTarget : scale, engraving };
            if (zoomAnim && scale > 1) return { phase: 'leaving', scale, engraving };
            if (pendingEntry && pendingEntry.generation === hoverIntentGeneration) {
                return { phase: 'pending', pointer: pendingEntry.pointer };
            }
            return null;
        };

        /* Carry on what the predecessor's hover was doing, in the task that
           seated this card. A grow or a shrink continues from the carried
           scale (never from rest), and an enlarged card gets back what an
           entry gives it: its raster lock, its Stellar hover and its hint. */
        const resume = (state) => {
            if (!state) return;
            if (state.phase === 'pending') {
                const intentGeneration = ++hoverIntentGeneration;
                const pointer = state.pointer;
                const event = pointer ? { clientX: pointer.x, clientY: pointer.y } : undefined;
                const blockedBy = hoverBlockReason();
                if (blockedBy) {
                    queueDeferredHover(event, blockedBy, intentGeneration);
                    return;
                }
                startHover(event, intentGeneration);
                return;
            }
            if (state.scale > 1) writeHoverScale(cardElement, state.scale);
            if (state.phase === 'leaving') {
                animateZoom(state.scale, 1, 150, () => clearHoverScale(cardElement));
                return;
            }
            engaged = true;
            // A drag (or a settling drop) owns the board: card-drag.js has
            // cleared every card's zoom and an entry would be refused, so a
            // rebuild starts no hover presentation (hint, Stellar) under it.
            if (hoverBlockReason()) return;
            // An entry would not enlarge an unlocked raster; this card already
            // is enlarged, so it is locked as soon as its paint allows.
            const lock = Cards?.lockCardRaster;
            if (typeof lock === 'function' && lock(cardElement, 'hover') === false) {
                const prepare = Cards?.prepareCardRasterForInteraction;
                if (typeof prepare === 'function') {
                    void Promise.resolve(prepare(cardElement, 'hover')).catch(() => false);
                }
            }
            if (Cards && typeof Cards.setStellarHover === 'function') {
                Cards.setStellarHover(cardElement, true);
            }
            // A carried grow places the hint when it lands, as an entry does;
            // a card already at its size keeps its hint in this task.
            const placeHint = hintPlacer(hoverIntentGeneration);
            if (state.target !== state.scale) animateZoom(state.scale, state.target, 200, placeHint);
            else placeHint();
        };

        return { teardown, hoverState, resume };
    }

    function shouldRequestActionIntentFallback(dropData) {
        if (!dropData || !dropData.instanceId) return false;
        const fromBoard = dropData.sourceType === 'board' || String(dropData.sourceZone || '').startsWith('player-board-slot');
        if (!fromBoard) return false;
        const state = typeof GameState !== 'undefined' && typeof GameState.get === 'function' ? GameState.get() : null;
        if (!state || !state.isYourTurn || state.actionInProgress) return false;
        const cards = state.player && Array.isArray(state.player.summonedCards) ? state.player.summonedCards : [];
        if (typeof GameState.canUseCard === 'function' && !GameState.canUseCard(dropData.instanceId)) return false;
        // Every commanded board Character gets the intent step — no cost gate.
        return cards.some(card => card && card.instanceId === dropData.instanceId);
    }

    function routeActionDrop(dropData, targetType, targetId, targetIndex) {
        if (ActionIntent && typeof ActionIntent.routeOrPlay === 'function') {
            return ActionIntent.routeOrPlay(dropData, targetType, targetId, targetIndex);
        }
        if (ActionIntent && typeof ActionIntent.request === 'function' && shouldRequestActionIntentFallback(dropData)) {
            return ActionIntent.request(dropData.instanceId, targetType, targetId, targetIndex);
        }
        if (Number.isInteger(targetIndex)) {
            return SocketHandler.playAction(dropData.instanceId, targetType, targetId, targetIndex);
        }
        return SocketHandler.playAction(dropData.instanceId, targetType, targetId);
    }

    /**
     * Setup drag-and-drop functionality for a card on the board
     * @param {HTMLElement} cardElement - Card DOM element
     * @param {Object} card - Card data object
     */
    function setupBoardCardDragging(cardElement, card, slotIndex) {
        CardDrag.cleanupDraggable(cardElement);
        cardElement.removeAttribute('draggable');
        cardElement.querySelectorAll('*').forEach(c => c.removeAttribute('draggable'));
        /* Every own Character can be PICKED UP (a refused lift says why), but
           only one that can act now wears the `draggable` look — the hover lift
           and grab cursor that invite the gesture. A card that cannot act keeps
           its plain hover rendering, as it always had. */
        const canActNow = typeof GameState !== 'undefined'
            && GameState.get?.()?.isYourTurn === true
            && typeof GameState.canUseCard === 'function'
            && GameState.canUseCard(card.instanceId) === true;
        cardElement.classList.toggle('draggable', canActNow);

        const slotEl = cardElement.closest('.card-slot');
        const idx = Number.isInteger(slotIndex)
            ? slotIndex
            : (slotEl ? slotEl.dataset.slotIndex : '0');
        CardDrag.makeDraggable(cardElement, CardPlay.boardDragConfig(card, idx));
    }

    /**
     * Setup board drop targets for card interactions
     */
    function setupBoardDropTargets() {
        // Board card targets are registered per-card in renderBoard; the
        // battlefield ZONES a summon or an untargeted cast is dropped into are
        // registered here, once the whole import graph exists.
        CardPlay.registerZones();
    }

    /**
     * Setup deck drop targets for card interactions
     */
    function setupDeckDropTargets() {
        const playerDeck = document.getElementById('player-deck');
        const opponentDeck = document.getElementById('opponent-deck');

        if (playerDeck) {
            CardDrag.registerTarget(playerDeck, {
                zone: 'player-deck',
                validate: (d) => d && GameState.get().isYourTurn,
                onDrop: (d) => {
                    const s = GameState.get();
                    if (s.actionInProgress) return;
                    return routeActionDrop(d, 'deck', s.playerId + '_deck');
                }
            });
        }

        if (opponentDeck) {
            CardDrag.registerTarget(opponentDeck, {
                zone: 'opponent-deck',
                validate: (d) => d && GameState.get().isYourTurn,
                onDrop: (d) => {
                    const s = GameState.get();
                    if (s.actionInProgress) return;
                    return routeActionDrop(d, 'deck', s.opponentId + '_deck');
                }
            });
        }
    }

    /**
     * Check if the game is over by examining player health
     * @param {Object} gameState - Current game state
     */
    function checkGameOver(gameState) {
        // Skip if we don't have health data
        if (!gameState || !gameState.player || !gameState.opponent) {
            return;
        }

        // Check if game over was already detected to prevent multiple triggers
        const gameOverAlreadyDetected = document.getElementById('game-over-popup');
        if (gameOverAlreadyDetected) {
            return;
        }

        if (gameState.state === 'ended') {
            const playerId = gameState.player.id;
            const opponentId = gameState.opponent.id;
            if (gameState.winner !== playerId && gameState.winner !== opponentId) return;
            GameEnd?.showGameOverScreen(gameState.winner === playerId, {
                canRematch: gameState.canRematch === true,
            });
            return;
        }
    }

    // Public API
    // Compose the #active-player status line. Extracted as a pure function so
    // the summon-silent rule — no "(Processing action…)" suffix while a quiet
    // action (e.g. the player's own summon) is latched — is unit testable
    // without standing up the whole board render.
    function activePlayerStatusText(gameState, silent) {
        // Key-aware: `t()` answers with the KEY when a translation is missing or
        // not loaded yet, and a key is truthy, so `|| fallback` alone rendered
        // `game.processingAction` to the player.
        const t = (key, fallback) => {
            const translated = i18n?.t?.(key);
            return translated && translated !== key ? translated : fallback;
        };
        const actionInProgress = !!(gameState && gameState.actionInProgress);
        if (!gameState.isYourTurn && actionInProgress) {
            return t('game.opponentThinking', 'Opponent is thinking...');
        }
        let text = gameState.isYourTurn
            ? t('game.yourTurn', 'Your Turn')
            : t('game.opponentTurn', "Opponent's Turn");
        if (actionInProgress && !silent) {
            text += ` (${t('game.processingAction', 'Processing action...')})`;
        }
        return text;
    }

    return {
        renderGameState,
        renderBoard,
        waitForCardPresentation,
        setupBoardDropTargets,
        setupDeckDropTargets,
        activePlayerStatusText
    };
})();

export { Board, HighlightUtil };
